import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider, Grid } from '@mui/material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { api } from '../../utils/api';

const QuotePrint = ({ quote, customer, vehicle, items, totals }) => {
  const currentDate = new Date();
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Recupero i dati aziendali dalla tabella CompanySettings
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        setIsLoading(true);
        const companyId = localStorage.getItem('companyId');
        if (!companyId) {
          throw new Error('CompanyId not found in localStorage');
        }

        const response = await api.get('/company-settings', {
          headers: {
            'x-company-id': companyId
          }
        });
        
        // Mappa i dati dalle impostazioni al formato richiesto
        setCompanyData({
          name: response.data.companyName || 'FreeWheel',
          address: response.data.companyAddress || '',
          phone: response.data.companyPhone || '',
          city: response.data.companyCity || '',
          postalCode: response.data.companyPostalCode || '',
          province: response.data.companyProvince || '',
          email: response.data.companyEmail || '',
          vatNumber: response.data.companyVatNumber || ''
        });
      } catch (error) {
        console.error('Errore nel caricamento dei dati aziendali:', error);
        // Fallback ai dati di default
        setCompanyData({
          name: 'FreeWheel',
          address: '',
          phone: '',
          city: '',
          postalCode: '',
          province: '',
          email: '',
          vatNumber: ''
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanySettings();
  }, []);

  if (isLoading) {
    return <div>Caricamento dati aziendali...</div>;
  }

  return (
    <Box
      id="quote-print"
      sx={{
        width: '210mm',
        minHeight: '297mm',
        padding: '20mm',
        backgroundColor: 'white',
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        lineHeight: 1.4,
        color: '#000',
        boxSizing: 'border-box'
      }}
    >
      {/* Header con info aziendali e numero preventivo */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 3,
        maxWidth: '100%'
      }}>
        {/* Dati aziendali a sinistra */}
        <Box sx={{ maxWidth: '65%' }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            mb: 0.5, 
            fontSize: '18px',
            color: '#DB1111',
            wordBreak: 'break-word'
          }}>
            {companyData?.name || 'FreeWheel'}
          </Typography>
          <Box sx={{ 
            fontSize: '10px', 
            color: '#666',
            lineHeight: 1.1
          }}>
            {companyData?.address && (
              <Typography variant="body2" sx={{ fontSize: '10px', mb: 0.2, wordBreak: 'break-word' }}>
                {companyData.address}
              </Typography>
            )}
            {(companyData?.postalCode || companyData?.city) && (
              <Typography variant="body2" sx={{ fontSize: '10px', mb: 0.2 }}>
                {[companyData.postalCode, companyData.city].filter(Boolean).join(' ')}
                {companyData?.province && ` (${companyData.province})`}
              </Typography>
            )}
            {companyData?.phone && (
              <Typography variant="body2" sx={{ fontSize: '10px', mb: 0.2 }}>
                Tel: {companyData.phone}
              </Typography>
            )}
            {companyData?.email && (
              <Typography variant="body2" sx={{ fontSize: '10px', mb: 0.2 }}>
                Email: {companyData.email}
              </Typography>
            )}
            {companyData?.vatNumber && (
              <Typography variant="body2" sx={{ fontSize: '10px', mb: 0.2 }}>
                P.IVA: {companyData.vatNumber}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Numero preventivo a destra */}
        <Box sx={{
          backgroundColor: '#f8f8f8',
          padding: '8px',
          borderRadius: '4px',
          border: '1px solid #eee',
          minWidth: '120px',
          textAlign: 'center'
        }}>
          <Typography variant="body2" sx={{ fontSize: '9px', color: '#666', mb: 0.5 }}>
            Preventivo N.:
          </Typography>
          <Typography variant="h6" sx={{ 
            fontSize: '14px', 
            fontWeight: 'bold', 
            color: '#DB1111' 
          }}>
            {quote?.quoteNumber || 'N/A'}
          </Typography>
        </Box>
      </Box>

      {/* Titolo documento */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ 
          fontWeight: 'bold', 
          fontSize: '20px',
          color: '#333'
        }}>
          PREVENTIVO
        </Typography>
      </Box>

      {/* Informazioni data e validità */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Data preventivo:
          </Typography>
          <Typography variant="body2">
            {format(currentDate, 'dd/MM/yyyy', { locale: it })}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Validità:
          </Typography>
          <Typography variant="body2">
            30 giorni
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Informazioni cliente e veicolo */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontSize: '14px' }}>
              CLIENTE
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {customer?.firstName} {customer?.lastName}
            </Typography>
            {customer?.phone && (
              <Typography variant="body2">
                Tel: {customer.phone}
              </Typography>
            )}
            {customer?.email && (
              <Typography variant="body2">
                Email: {customer.email}
              </Typography>
            )}
            {customer?.address && (
              <Typography variant="body2">
                {customer.address}
              </Typography>
            )}
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontSize: '14px' }}>
              VEICOLO
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {vehicle?.brand} {vehicle?.model}
            </Typography>
            {vehicle?.type && (
              <Typography variant="body2">
                Tipo: {vehicle.type}
              </Typography>
            )}
            {vehicle?.color && (
              <Typography variant="body2">
                Colore: {vehicle.color}
              </Typography>
            )}
            {vehicle?.serialNumber && (
              <Typography variant="body2">
                Numero di serie: {vehicle.serialNumber}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Descrizione preventivo */}
      {quote?.description && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontSize: '14px' }}>
            DESCRIZIONE LAVORI
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {quote.description}
          </Typography>
        </Box>
      )}

      {/* Tabella prodotti */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontSize: '14px' }}>
          DETTAGLIO PREVENTIVO
        </Typography>
        
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '11px', padding: '8px' }}>
                  Codice
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '11px', padding: '8px' }}>
                  Descrizione
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '11px', padding: '8px' }}>
                  Q.tà
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '11px', padding: '8px' }}>
                  Prezzo Unit.
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '11px', padding: '8px' }}>
                  Sconto
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '11px', padding: '8px' }}>
                  Totale
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items?.map((item, index) => {
                const itemTotal = item.quantity * item.unitPrice;
                const discountAmount = parseFloat(item.discount) || 0; // Sconto in euro invece che percentuale
                const finalTotal = itemTotal - discountAmount;
                
                return (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: '11px', padding: '8px' }}>
                      {item.productCode}
                    </TableCell>
                    <TableCell sx={{ fontSize: '11px', padding: '8px' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {item.productName}
                        </Typography>
                        {item.productDescription && (
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {item.productDescription}
                          </Typography>
                        )}
                        {item.includesLabor && (
                          <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                            (Con montaggio)
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '11px', padding: '8px' }}>
                      {item.quantity}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '11px', padding: '8px' }}>
                      €{item.unitPrice ? parseFloat(item.unitPrice).toFixed(2) : '0.00'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '11px', padding: '8px' }}>
                      {item.discount > 0 ? `€${parseFloat(item.discount).toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '11px', padding: '8px', fontWeight: 'bold' }}>
                      €{finalTotal.toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Totali */}
      <Box sx={{ mt: 4, textAlign: 'right' }}>
        <Box sx={{ display: 'inline-block', minWidth: '300px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '12px' }}>
              Subtotale:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              €{totals?.subtotal?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '12px' }}>
              IVA ({totals?.taxRate || 22}%) inclusa:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              €{totals?.taxAmount?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 1 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 'bold' }}>
              TOTALE:
            </Typography>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 'bold', color: '#1976d2' }}>
              €{totals?.total?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Note e condizioni */}
      <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid #ddd' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontSize: '14px' }}>
          NOTE E CONDIZIONI
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '11px', lineHeight: 1.5 }}>
          • Il presente preventivo ha validità di 30 giorni dalla data di emissione<br/>
          • I prezzi sono espressi in Euro e sono comprensivi di IVA al 22%<br/>
          • In caso di accettazione, sarà richiesto un acconto del 50%<br/>
          • I tempi di consegna sono indicativi e possono variare in base alla disponibilità dei ricambi<br/>
          • La garanzia sui ricambi segue le condizioni del produttore<br/>
          • Eventuali lavori aggiuntivi non previsti nel preventivo saranno preventivati separatamente
        </Typography>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 6, textAlign: 'center', pt: 3, borderTop: '1px solid #ddd' }}>
        <Typography variant="body2" sx={{ fontSize: '10px', color: '#666' }}>
          Preventivo generato il {format(currentDate, 'dd/MM/yyyy HH:mm', { locale: it })}<br/>
          Per informazioni contattare il nostro servizio clienti
        </Typography>
      </Box>
    </Box>
  );
};

export default QuotePrint;
